import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {Component, HostListener, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NgbActiveModal, NgbModal, NgbModalOptions, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {TicketComponentPart, TicketOperationResult} from '@app/layout/sites/tickets/tickets.interface';
import {AlertTicket, Ticket} from '@shared/services';
import {SiteInfo} from '@shared/services/api/access-sites.interface';
import {TicketArchivedDisplayState, TicketUtils} from '@app/layout/sites/tickets/ticket-utils';
import {defaultTicketModalOptions, TicketDetailsModal, TicketModalUtil} from '@app/layout/sites/tickets/ticket-modals/ticket-modal-util';


export interface AlertTicketDetailsModal extends TicketDetailsModal<AlertTicket> {}


@Component({
  selector: 'app-alert-ticket-details-modal',
  templateUrl: './alert-ticket-details-modal.component.html',
  styleUrls: ['./alert-ticket-details-modal.component.scss'],
  standalone: false
})
export class AlertTicketDetailsModalComponent implements OnInit, OnDestroy, AlertTicketDetailsModal {

  @Input() ticket: AlertTicket;
  @Input() site: SiteInfo;
  @Input() hideInboxControl = false;
  @Input() showSiteName = false;
  @Input() showSiteNameAsLink = true;
  @Input() showViewLinkedTicket = true;
  @Input() showOtherEscalatedTicketsCount = false;
  @Input() showOtherEscalatedTicketsCountAsLink = false;
  @Input() displayArchived: TicketArchivedDisplayState = TicketArchivedDisplayState.NOT_ARCHIVED;
  @Input() readOnly = false;

  @ViewChild('ticketSummaryComponent', { static: true })
  ticketSummaryComponent: TicketComponentPart;

  @ViewChild('ticketBodyComponent', { static: true })
  ticketBodyComponent: TicketComponentPart;

  ticketOperationResult: TicketOperationResult<Ticket>;

  private readonly ngUnsubscribe: Subject<void> = new Subject();
  // Track if another modal has been opened, if so do not close this modal on ESC key
  private canCloseModal = true;

  static openInModal(modalService: NgbModal, data: AlertTicketDetailsModal, ngbOptions?: NgbModalOptions): NgbModalRef {
    const modalRef = modalService.open(AlertTicketDetailsModalComponent, {
      ...defaultTicketModalOptions,
      ...ngbOptions
    });
    const modalInstance: AlertTicketDetailsModalComponent = modalRef.componentInstance;

    TicketModalUtil.inflateModalInstance(modalInstance, data);

    if (data.showSiteName != null) {
      modalInstance.showSiteName = data.showSiteName;
    }
    if (data.showSiteNameAsLink != null) {
      modalInstance.showSiteNameAsLink = data.showSiteNameAsLink;
    }
    if (data.hideInboxControl != null) {
      modalInstance.hideInboxControl = data.hideInboxControl;
    }
    if (data.showViewLinkedTicket != null) {
      modalInstance.showViewLinkedTicket = data.showViewLinkedTicket;
    }
    if (data.showOtherEscalatedTicketsCount != null) {
      modalInstance.showOtherEscalatedTicketsCount = data.showOtherEscalatedTicketsCount;
    }
    if (data.showOtherEscalatedTicketsCountAsLink != null) {
      modalInstance.showOtherEscalatedTicketsCountAsLink = data.showOtherEscalatedTicketsCountAsLink;
    }
    if (data.displayArchived != null) {
      modalInstance.displayArchived = data.displayArchived;
    }
    if (data.readOnly != null) {
      modalInstance.readOnly = data.readOnly;
    }

    return modalRef;
  }

  constructor(private activeModal: NgbActiveModal, private modalService: NgbModal) {}

  ngOnInit(): void {
    this.modalService.activeInstances.pipe(takeUntil(this.ngUnsubscribe)).subscribe(modals => {
      this.canCloseModal = !(modals?.length > 1);
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  // returns boolean to indicate whether ticket details modal would stay open as result of this ticket operation
  private onTicketOperation(result: TicketOperationResult<AlertTicket>): boolean {
    if (TicketUtils.closeModal(result, this.displayArchived)) {
      this.activeModal.close(result);
      return false;
    }
    this.ticketOperationResult = TicketUtils.mergeTicketOperationResults(this.ticketOperationResult, result);
    return true;
  }

  onTicketOperationFromSummaryComponent(result: TicketOperationResult<AlertTicket>): void {
    const refreshOtherComponents = this.onTicketOperation(result);
    if (refreshOtherComponents) {
      // refresh body component if detail window remains open
      this.ticketBodyComponent.afterTicketUpdate(this.ticketOperationResult);
    }
  }

  onTicketOperationFromBodyComponent(result: TicketOperationResult<AlertTicket>): void {
    const refreshOtherComponents = this.onTicketOperation(result);
    if (refreshOtherComponents) {
      // refresh summary component if detail window remains open
      this.ticketSummaryComponent.afterTicketUpdate(this.ticketOperationResult);
    }
  }

  closeModal() {
    if (this.ticketOperationResult) {
      this.activeModal.close(this.ticketOperationResult);
    } else {
      this.activeModal.dismiss();
    }
  }

  @HostListener('window:keyup.escape', ['$event'])
  keyEvent(_event: KeyboardEvent) {
    // Need to return updated ticket even if Escape is pressed on the keyboard. So handle our own keyboard events
    // Track if any nested modals have been opened (Ticket Edit Modal) and only close this modal if there are no nested
    // modals
    if (this.canCloseModal) {
      this.closeModal();
    }
  }
}
